import {
  CustomGridParameters,
  Direction,
  DirectionEnum,
  GridType,
  GridTypeEnum,
  OrderSide,
  OrderSideEnum,
} from "../_app/types";
import { Order } from "./Order";
import { _roundToDecimals } from "../utils/_roundToDecimals";

export class OrderListBuilder {
  private readonly useCustomGrid: boolean;

  private readonly gridType: GridType;

  private readonly gridDirection: Direction;

  private readonly orderStep: number;

  private readonly priceStep: number;

  private readonly startPrice: number;

  private readonly numberOfGrids: number;

  private readonly priceStepPrecision: number; // Natural number (1,2,3...)

  private readonly customGridParameters?: CustomGridParameters;

  constructor(
    gridType: GridType,
    orderStep: number,
    gridDirection: Direction,
    priceStep: number,
    startPrice: number,
    numberOfGrids: number,
    priceStepPrecision: number,
    customGridParameters?: CustomGridParameters
  ) {
    this.gridType = gridType;
    this.orderStep = orderStep;
    this.gridDirection = gridDirection;
    this.priceStep = priceStep;
    this.startPrice = startPrice;
    this.numberOfGrids = numberOfGrids;
    this.priceStepPrecision = priceStepPrecision;
    this.customGridParameters = customGridParameters;
    this.useCustomGrid = !!customGridParameters;
  }

  public createOrders(): Order[] {
    return this.createGrid();
  }

  private createGrid(): Order[] {
    switch (this.gridType) {
      case GridTypeEnum.Arithmetic:
        return this.createAlgebraicGrid();
      case GridTypeEnum.Geometric:
        return this.createGeometricGrid();
      default:
        throw new Error("Invalid grid type");
    }
  }
  //
  // private createCustomGrid(): Order[] {
  //     return this.customGridParameters!.gridOrderSettings.map(orderSetting => new Order(
  //         orderSetting.openPrice,
  //         orderSetting.closePrice,
  //         orderSetting.orderSide,
  //         this.orderStep
  //     ));
  // }

  private createAlgebraicGrid(): Order[] {
    switch (this.gridDirection) {
      case DirectionEnum.Central:
        return this.createCentralAlgebraicGrid();
      case DirectionEnum.Long:
        return this.createLongAlgebraicGrid();
      case DirectionEnum.Short:
        return this.createShortAlgebraicGrid();
      default:
        throw new Error("Invalid grid direction");
    }
  }

  private createCentralAlgebraicGrid(): Order[] {
    let currentBuyPrice = this.startPrice - this.priceStep;
    let currentSellPrice = this.startPrice + this.priceStep;

    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids / 2; i++) {
      const longOrderItem = new Order(
        currentBuyPrice,
        currentBuyPrice + this.priceStep,
        OrderSideEnum.Long,
        this.orderStep
      );

      currentBuyPrice -= this.priceStep;
      result.push(longOrderItem);

      const shortOrderItem = new Order(
        currentSellPrice,
        currentSellPrice - this.priceStep,
        OrderSideEnum.Short,
        this.orderStep
      );

      currentSellPrice += this.priceStep;
      result.push(shortOrderItem);
    }

    return result;
  }

  private createLongAlgebraicGrid(): Order[] {
    let currentBuyPrice = this.startPrice - this.priceStep;

    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids; i++) {
      const longOrderItem = new Order(
        currentBuyPrice,
        currentBuyPrice + this.priceStep,
        OrderSideEnum.Long,
        this.orderStep
      );

      currentBuyPrice -= this.priceStep;
      result.push(longOrderItem);
    }

    return result;
  }

  private createShortAlgebraicGrid(): Order[] {
    let currentSellPrice = this.startPrice + this.priceStep;

    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids; i++) {
      const shortOrderItem = new Order(
        currentSellPrice,
        currentSellPrice - this.priceStep,
        OrderSideEnum.Short,
        this.orderStep
      );

      currentSellPrice += this.priceStep;
      result.push(shortOrderItem);
    }

    return result;
  }

  private createGeometricGrid(): Order[] {
    switch (this.gridDirection) {
      case DirectionEnum.Central:
        return this.createCentralGeometricGrid();
      case DirectionEnum.Long:
        return this.createLongGeometricGrid();
      case DirectionEnum.Short:
        return this.createShortGeometricGrid();
      default:
        throw new Error("Invalid grid direction");
    }
  }

  private createCentralGeometricGrid(): Order[] {
    // let currentBuyPrice = Math.round(this.startPrice / (1 + this.priceStep)) / Math.pow(10, this.priceStepPrecision);
    // let currentSellPrice = Math.round(this.startPrice / (1 + this.priceStep)) / Math.pow(10, this.priceStepPrecision);
    // let currentBuyPrice = Math.round(this.startPrice / this.priceStep * Math.pow(10, this.priceStepPrecision)) / Math.pow(10, this.priceStepPrecision);
    // let currentSellPrice = Math.round(this.startPrice * this.priceStep * Math.pow(10, this.priceStepPrecision)) / Math.pow(10, this.priceStepPrecision);

    let currentBuyPrice = this.startPrice;
    let currentSellPrice = this.startPrice;

    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids / 2; i++) {
      const longOrderItem = new Order(
        _roundToDecimals(currentBuyPrice / (1 + this.priceStep), this.priceStepPrecision),
        currentBuyPrice,
        OrderSideEnum.Long,
        this.orderStep
      );

      currentBuyPrice = _roundToDecimals(
        currentBuyPrice / (1 + this.priceStep),
        this.priceStepPrecision
      );
      result.push(longOrderItem);

      const shortOrderItem = new Order(
        _roundToDecimals(currentSellPrice / (1 - this.priceStep), this.priceStepPrecision),
        currentSellPrice,
        OrderSideEnum.Short,
        this.orderStep
      );

      currentSellPrice = _roundToDecimals(
        currentSellPrice / (1 - this.priceStep),
        this.priceStepPrecision
      );
      result.push(shortOrderItem);
    }

    return result;
  }

  private createLongGeometricGrid(): Order[] {
    let currentBuyPrice = this.startPrice;
    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids; i++) {
      const longOrderItem = new Order(
        currentBuyPrice,
        _roundToDecimals(currentBuyPrice / (1 + this.priceStep), this.priceStepPrecision),
        OrderSideEnum.Long,
        this.orderStep
      );

      currentBuyPrice = _roundToDecimals(
        currentBuyPrice / (1 + this.priceStep),
        this.priceStepPrecision
      );
      result.push(longOrderItem);
    }

    return result;
  }

  private createShortGeometricGrid(): Order[] {
    let currentSellPrice = this.startPrice;

    const result: Order[] = [];

    for (let i = 0; i < this.numberOfGrids; i++) {
      const shortOrderItem = new Order(
        _roundToDecimals(currentSellPrice / (1 - this.priceStep), this.priceStepPrecision),
        currentSellPrice,
        OrderSideEnum.Short,
        this.orderStep
      );
      currentSellPrice = _roundToDecimals(
        currentSellPrice / (1 - this.priceStep),
        this.priceStepPrecision
      );
      // currentSellPrice = Math.round(currentSellPrice * this.priceStep * Math.pow(10, this.priceStepPrecision)) / Math.pow(10, this.priceStepPrecision);
      result.push(shortOrderItem);
    }

    return result;
  }
}
